import { createFileRoute, redirect } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { z } from "zod";

import { IngredientsOverview } from "@/components/ingredient-db/ingredients-overview";
import {
  INGREDIENT_CATEGORY_FILTERS,
  INGREDIENT_SOURCE_FILTERS,
} from "@/components/ingredient-db/ingredient-search-params";
import { ContainerSmall } from "@/components/primitives/container-small";
import { PageContainer } from "@/components/primitives/layout";
import { auth } from "@/lib/auth";
import {
  getCachedPopularIngredients,
  getUserIngredientOverview,
} from "@/lib/ingredients/ingredientOverview.server";
import { optionalStringSearchParam } from "@/lib/searchParams";

const legacySearchSchema = z.object({
  view: optionalStringSearchParam,
  query: optionalStringSearchParam,
  category: optionalStringSearchParam,
  source: optionalStringSearchParam,
  page: z.union([z.string(), z.number()]).optional().catch(undefined),
});
const legacyCategorySchema = z.enum(INGREDIENT_CATEGORY_FILTERS);
const legacySourceSchema = z.enum(INGREDIENT_SOURCE_FILTERS);

function getLegacyCategory(value: string | undefined) {
  const result = legacyCategorySchema.safeParse(value);
  return result.success ? result.data : undefined;
}

function getLegacySource(value: string | undefined) {
  const result = legacySourceSchema.safeParse(value);
  return result.success ? result.data : undefined;
}

const getIngredientsOverviewData = createServerFn({ method: "GET" }).handler(
  async () => {
    const request = getRequest();
    const sessionPromise = auth.api.getSession({ headers: request.headers });
    const popularIngredientsPromise = getCachedPopularIngredients({
      limit: 18,
    });
    const topSweetenersPromise = getCachedPopularIngredients({
      category: "sweetener",
      limit: 6,
    });
    const topStabilizersPromise = getCachedPopularIngredients({
      category: "stabilizer",
      limit: 6,
    });
    const topFruitPromise = getCachedPopularIngredients({
      category: "fruit",
      limit: 6,
    });
    const topChocolatePromise = getCachedPopularIngredients({
      category: "chocolate",
      limit: 6,
    });
    const topNutsAndSeedsPromise = getCachedPopularIngredients({
      category: "nut",
      limit: 6,
    });
    const topOtherPromise = getCachedPopularIngredients({
      category: "other",
      limit: 6,
    });
    const topDairyPromise = getCachedPopularIngredients({
      category: "dairy",
      limit: 6,
    });
    const topEmulsifiersPromise = getCachedPopularIngredients({
      category: "emulsifier",
      limit: 6,
    });
    const session = await sessionPromise;
    const userId = session?.user?.id ?? null;
    const [
      popularIngredients,
      topSweeteners,
      topStabilizers,
      topFruit,
      topChocolate,
      topNutsAndSeeds,
      topOther,
      topDairy,
      topEmulsifiers,
      userOverview,
    ] = await Promise.all([
      popularIngredientsPromise,
      topSweetenersPromise,
      topStabilizersPromise,
      topFruitPromise,
      topChocolatePromise,
      topNutsAndSeedsPromise,
      topOtherPromise,
      topDairyPromise,
      topEmulsifiersPromise,
      userId
        ? getUserIngredientOverview({ userId })
        : Promise.resolve({ ingredients: [], total: 0 }),
    ]);

    return {
      userId,
      popularIngredients,
      topSweeteners,
      topStabilizers,
      topFruit,
      topChocolate,
      topNutsAndSeeds,
      topOther,
      topDairy,
      topEmulsifiers,
      myIngredients: userOverview.ingredients,
      myIngredientsTotal: userOverview.total,
    };
  },
);

export const Route = createFileRoute("/ingredients/")({
  validateSearch: legacySearchSchema,
  beforeLoad: ({ search }) => {
    const shouldRedirect =
      search.view === "mine" ||
      search.query !== undefined ||
      search.category !== undefined ||
      search.source !== undefined;

    if (shouldRedirect) {
      throw redirect({
        to: "/ingredients/search",
        search: {
          q: search.query?.trim() || undefined,
          category: getLegacyCategory(search.category),
          source:
            search.view === "mine" ? "user" : getLegacySource(search.source),
        },
        replace: true,
      });
    }
  },
  head: () => ({
    meta: [
      { title: "Ice Cream Ingredient Database - Scoopulator" },
      {
        name: "description",
        content:
          "Browse ice cream ingredients and compare FPDF (PAC), sweetness (POD), nutrition, and composition (fat, carbs, sugar, protein, water).",
      },
    ],
  }),
  loader: () => getIngredientsOverviewData(),
  component: IngredientsPage,
});

function IngredientsPage() {
  const data = Route.useLoaderData();

  return (
    <PageContainer>
      <ContainerSmall>
        <IngredientsOverview {...data} />
      </ContainerSmall>
    </PageContainer>
  );
}
